import { AppCustomerSelectPage } from "app/AppCustomerSelectPage";
import { AppLoggedOutLayout } from "app/AppLoggedOutLayout";
import { AuthIFrameClientPage } from "features/auth/AuthIFrameClientPage";
import { useIsLoggedIn } from "features/auth/AuthUtils";
import { usePing } from "gen/endpoints/frontend/frontend";
import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { GenericErrorPageView } from "shared/components/empty/GenericErrorPageView";
import { LoadingState } from "shared/components/empty/LoadingState";
import { LoggedInLayout } from "shared/components/layout/LoggedInLayout";
import { useToast } from "shared/components/toast/useToast";
import { AppConfig } from "shared/config/AppConfig";
import { useEffectOnce } from "shared/hooks/useEffectOnce";
import { rootRoute } from "shared/navigation/Routes";

const accountPath = rootRoute({}).account({}).$;
const internalPath = rootRoute({}).internal({}).$;

export const AuthRequired: React.FC = () => {
  const location = useLocation();
  const toast = useToast();
  const { t } = useTranslation(["auth"]);
  const isLoggedIn = useIsLoggedIn();

  const { isLoading, error, refetch } = usePing({
    query: {
      throwOnError: false,
      enabled: isLoggedIn,
      // Keep session alive. axios-jwt will automatically refresh the token if it's expired
      refetchInterval: 1000 * 60 * 5, // 5 minutes
    },
  });

  useEffectOnce(() => {
    if (!isLoggedIn && location.pathname !== "/") {
      toast.showToast({
        message: t("auth:errors.loginRequired"),
        variant: "warning",
      });
    }
  });

  if (!isLoggedIn) {
    if (AppConfig.subdomains?.isSubdomain) {
      return <AuthIFrameClientPage />;
    }
    return (
      <AppLoggedOutLayout>
        <Navigate
          to={rootRoute({}).auth({}).login({}).$}
          state={{ from: location }}
          replace={true}
        />
      </AppLoggedOutLayout>
    );
  }

  if (error) {
    return (
      <AppLoggedOutLayout>
        <GenericErrorPageView
          error={error}
          reset={refetch}
          showSignOut={true}
        />
      </AppLoggedOutLayout>
    );
  }

  if (isLoading) {
    return (
      <AppLoggedOutLayout>
        <LoadingState />
      </AppLoggedOutLayout>
    );
  }

  const isInternalAdminDashboardView =
    location.pathname.startsWith(internalPath);
  if (
    AppConfig.subdomains?.isAuthOnly &&
    !location.pathname.startsWith(accountPath) &&
    !isInternalAdminDashboardView
  ) {
    return (
      <LoggedInLayout>
        <AppCustomerSelectPage />
      </LoggedInLayout>
    );
  }

  return (
    <LoggedInLayout isInternalAdminDashboardView={isInternalAdminDashboardView}>
      <Outlet />
    </LoggedInLayout>
  );
};
